import { useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { useRequireRole } from "@/lib/use-require-role";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { organizations, plans, fmtCents } from "@/mocks/saas-data";
import { impersonate, mockUsers } from "@/lib/mock-auth";

export const Route = createFileRoute("/admin/organizations/$id")({
  ssr: false,
  head: () => ({ meta: [{ title: "Admin — Organization" }] }),
  component: Page,
});

function Page() {
  const user = useRequireRole(["superadmin"]);
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const org = organizations.find((o) => o.id === id);
  const [tick, setTick] = useState(0);

  if (!user) return null;
  if (!org) {
    return <div className="mx-auto max-w-2xl p-10 text-center text-sm text-muted-foreground">Organization not found.</div>;
  }
  const members = mockUsers.filter((u) => u.orgId === org.id);
  const owner = members.find((m) => m.id === org.ownerUserId);

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader
        title={org.name}
        description={`/${org.slug} · ${org.status} · created ${org.createdAt}`}
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                if (!owner) return toast.error("No owner to impersonate");
                impersonate(owner.id);
                toast.success(`Impersonating ${org.name}`);
                navigate({ to: "/", replace: true });
              }}
            >
              Impersonate
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                org.status = org.status === "suspended" ? "active" : "suspended";
                toast.success(`Org ${org.status}`);
                setTick((t) => t + 1);
              }}
            >
              {org.status === "suspended" ? "Reactivate" : "Suspend"}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="rounded-xl bg-card p-6 ring-1 ring-border">
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Plan & billing</h2>
            <div className="grid grid-cols-2 gap-6 text-sm">
              <div>
                <p className="font-mono text-[10px] uppercase text-muted-foreground">Current plan</p>
                <p className="mt-1 font-medium capitalize">{org.planId}</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase text-muted-foreground">MRR</p>
                <p className="mt-1 font-mono font-medium">{fmtCents(org.mrrCents)}</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase text-muted-foreground">Lifetime revenue</p>
                <p className="mt-1 font-mono font-medium">{fmtCents(org.lifetimeRevenueCents)}</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase text-muted-foreground">Storage</p>
                <p className="mt-1 font-mono font-medium">{org.storageUsedGb} GB</p>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <span className="text-xs text-muted-foreground">Change plan</span>
              <Select
                value={org.planId}
                onValueChange={(v) => {
                  org.planId = v as typeof org.planId;
                  org.mrrCents = plans.find((p) => p.id === v)?.priceCents ?? 0;
                  toast.success(`Plan changed to ${v}`);
                  setTick((t) => t + 1);
                }}
              >
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {fmtCents(p.priceCents)}/mo</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <span className="hidden">{tick}</span>
          </section>

          <section className="rounded-xl bg-card ring-1 ring-border">
            <div className="border-b border-border px-5 py-4">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Members ({members.length})</h2>
            </div>
            <ul className="divide-y divide-border">
              {members.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                  <div>
                    <p className="font-medium">{m.name}</p>
                    <p className="font-mono text-[11px] text-muted-foreground">{m.email}</p>
                  </div>
                  <span className="font-mono text-[10px] uppercase text-muted-foreground">{m.role.replace("org_", "")}</span>
                </li>
              ))}
              {members.length === 0 && <li className="px-5 py-6 text-center text-xs text-muted-foreground">No members.</li>}
            </ul>
          </section>
        </div>

        <aside className="space-y-3">
          <div className="rounded-xl bg-card p-5 ring-1 ring-border">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Quick actions</h2>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start" onClick={() => toast.info("Trial extended 14d")}>Extend trial 14 days</Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => toast.info("Reset email sent to owner")}>Send password reset</Button>
              <Button variant="outline" className="w-full justify-start" onClick={() => toast.info("Exported")}>Export org data</Button>
              <Button variant="destructive" className="w-full justify-start" onClick={() => toast.error("Delete confirmation skipped (mock)")}>Delete organization</Button>
            </div>
          </div>
          <Link to="/admin/organizations" className="block text-center text-xs text-muted-foreground hover:underline">← Back to all organizations</Link>
        </aside>
      </div>
    </div>
  );
}
